import { TipoArquivo } from "@workflow/types";

import { ValidationRule } from "./types";

/** Regras específicas por tipo. Tipos ausentes não têm validação extra além do tamanho. */
export const validationRules: Partial<Record<TipoArquivo, ValidationRule>> = {
  AP: {
    allowedExtensions: [".pdf"],
  },
  COMPRAS: {
    nameMustContain: ["interno", "interna"],
  },
  INTERNO: {
    nameMustContain: ["interno", "interna"],
  },
  COMERCIAL: {
    nameMustContain: "comercial",
  },
  JOB_BRIEFING: {
    allowedExtensions: [".pdf", ".doc", ".docx"],
  },
};

/**
 * Teto padrão de tamanho no front (MB). **Espelha** o `DEFAULT_MAX_BYTES` do backend
 * (100 MB, em `domain/mappings/tipo-arquivo-validacao.mapping.ts`). Se o backend passar
 * a definir `maxBytes` por tipo, refletir aqui. Não usar 50 — era o teto antigo, antes
 * do upload direto via signed URL.
 */
export const DEFAULT_MAX_FILE_SIZE_MB = 100;

export type ValidationResult = { ok: true } | { ok: false; message: string };

/** Valida tamanho, extensão e nomenclatura do arquivo conforme as regras do tipo. */
export function validateFile(
  selectedFile: File,
  tipo: TipoArquivo,
  maxFileSizeMB: number,
): ValidationResult {
  // 1. Tamanho (global)
  const maxBytes = maxFileSizeMB * 1024 * 1024;
  if (selectedFile.size > maxBytes) {
    return { ok: false, message: `Limite de ${maxFileSizeMB} MB excedido.` };
  }

  const regra = validationRules[tipo];
  if (!regra) return { ok: true };

  // 2. Extensão
  if (regra.allowedExtensions && regra.allowedExtensions.length > 0) {
    const fileName = selectedFile.name.toLowerCase();
    const hasValidExtension = regra.allowedExtensions.some((ext) =>
      fileName.endsWith(ext.toLowerCase()),
    );

    if (!hasValidExtension) {
      return {
        ok: false,
        message: `Formato inválido. Permitido apenas: ${regra.allowedExtensions.join(", ").toUpperCase()}.`,
      };
    }
  }

  // 3. Nomenclatura
  if (regra.nameMustContain) {
    const fileName = selectedFile.name.toLowerCase();
    const terms = Array.isArray(regra.nameMustContain)
      ? regra.nameMustContain
      : [regra.nameMustContain];
    const hasTerm = terms.some((term) => fileName.includes(term.toLowerCase()));

    if (!hasTerm) {
      return {
        ok: false,
        message: `O nome do arquivo deve conter "${terms.join('" ou "').toUpperCase()}".`,
      };
    }
  }

  return { ok: true };
}

/**
 * Deriva o atributo `accept` do `<input>` a partir das regras do tipo.
 * Se um `accept` explícito for informado, ele tem prioridade.
 */
export function deriveAccept(type: TipoArquivo, accept?: string): string | undefined {
  if (accept) return accept;

  const regra = validationRules[type];
  if (!regra?.allowedExtensions) return undefined;

  const extensions = regra.allowedExtensions;
  const mimeTypes: string[] = [];

  if (extensions.includes(".pdf")) mimeTypes.push("application/pdf");
  if (extensions.includes(".doc") || extensions.includes(".docx")) {
    mimeTypes.push(
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    );
  }

  return [...extensions, ...mimeTypes].join(",");
}
